import { API_CONFIG, buildApiUrl, getAuthHeaders, getDefaultHeaders } from '../config/api.ts';
import { apiRequest } from '../utils/apiRequest.ts';

// User type as returned by the backoffice API
export type UserType = 'admin' | 'manager' | 'bartender';

// Types for API responses
export interface LoginRequest {
  email: string;
  password: string;
}

export interface ApiUser {
  id: number;
  email: string;
  type: UserType;
  isActive: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface LoginResponse {
  user: ApiUser;
  sessionToken: string;
}

export interface ValidateResponse {
  user: ApiUser;
}

export interface ApiError {
  message: string;
  statusCode: number;
}

// Password reset request types
export interface ResetPasswordRequest {
  email: string;
}

export interface ResetPasswordResponse {
  message: string;
}

export interface ResetPasswordWithTokenRequest {
  token: string;
  newPassword: string;
  confirmPassword: string;
}

export interface ResetPasswordWithTokenResponse {
  message: string;
}

// Authentication API calls
export const authApi = {
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    return apiRequest<LoginResponse>(
      buildApiUrl(API_CONFIG.ENDPOINTS.AUTH.LOGIN),
      {
        method: 'POST',
        headers: getDefaultHeaders(),
        body: JSON.stringify(credentials),
      }
    );
  },

  logout: async (token: string): Promise<{ message: string }> => {
    return apiRequest<{ message: string }>(
      buildApiUrl(API_CONFIG.ENDPOINTS.AUTH.LOGOUT),
      {
        method: 'POST',
        headers: getAuthHeaders(token),
      }
    );
  },

  validate: async (token: string): Promise<ValidateResponse> => {
    return apiRequest<ValidateResponse>(
      buildApiUrl(API_CONFIG.ENDPOINTS.AUTH.VALIDATE),
      {
        method: 'GET',
        headers: getAuthHeaders(token),
      }
    );
  },

  resetPassword: async (request: ResetPasswordRequest): Promise<ResetPasswordResponse> => {
    return apiRequest<ResetPasswordResponse>(
      buildApiUrl(API_CONFIG.ENDPOINTS.AUTH.RESET_PASSWORD),
      {
        method: 'POST',
        headers: getDefaultHeaders(),
        body: JSON.stringify(request),
      }
    );
  },

  resetPasswordWithToken: async (request: ResetPasswordWithTokenRequest): Promise<ResetPasswordWithTokenResponse> => {
    return apiRequest<ResetPasswordWithTokenResponse>(
      buildApiUrl('/auth/reset-password-with-token'),
      {
        method: 'POST',
        headers: getDefaultHeaders(),
        body: JSON.stringify(request),
      }
    );
  },
};

// Generic API call with authentication
export const authenticatedApiCall = async <T>(
  endpoint: string,
  options: RequestInit = {},
  token: string
): Promise<T> => {
  return apiRequest<T>(
    buildApiUrl(endpoint),
    {
      ...options,
      headers: {
        ...getAuthHeaders(token),
        ...options.headers,
      },
    }
  );
};
